<template>

<div>
  <div class="card integrity"
       v-if="isGranted([permissions.PROCESS_INTEGRITY_VIEW, permissions.PROCESS_INTEGRITY_VIEW_OWN], process)"
       qid="integrity-section"
       id="update_loss_of_integrity"
  >
    <div class="card-header" :class="{'pr-0 pl-0': showOnTask}">
      <div class="float-right">
        <ValidationForm
            :object="process"
            type="integrity"
            scope="validate_integrity_impact_and_probability"
            @validationModalClose="handleValidationModalClose"
            v-if="!showOnTask && isGranted([permissions.PROCESS_INTEGRITY_VALIDATE, permissions.PROCESS_INTEGRITY_VALIDATE_OWN], process)"
        ></ValidationForm>

        <span class="btn btn-success"
              @click="showFormFunction"
              qid="integrity-edit-button"
              v-if="isGranted([permissions.PROCESS_INTEGRITY_EDIT, permissions.PROCESS_INTEGRITY_EDIT_OWN], process)"
        >
            <font-awesome-icon icon="pen"/> {{ $t('system.edit') }}
          </span>
      </div>
      <div class="float-left">
        <h5 class="mb-2" qid="integrity-table-title" v-if="!showOnTask">
          {{ $t('processes.integrity.title') }}
        </h5>

        <span v-if="!showOnTask">
          <span class="cursor-pointer task-label-open mr-2"
                v-if="getTask('update_loss_of_integrity')"
          >
            <UpdateSingleTaskModal
                :task="getTask('update_loss_of_integrity')"
                @reloadContent="reloadContent"
                :editIcon="true"
            ></UpdateSingleTaskModal>
          </span>
          <span v-if="getGuide('update_loss_of_integrity')">
            <GuideButton :guide="getGuide('update_loss_of_integrity')"></GuideButton>
          </span>
        </span>
      </div>

      <div class="validation-info" qid="view-integrity-validation" v-if="process.integrity_validation && !showOnTask">
        <span v-if="process.integrity_validation && process.integrity_validation.validation === 1">
          <font-awesome-icon icon="check-circle" class="icon-success"/>
          {{ $t('system.approved') }}
        </span>
        <span v-if="process.integrity_validation && process.integrity_validation.validation === 0">
          <font-awesome-icon icon="times-circle" class="icon-danger"/>
          {{ $t('system.rejected') }}
        </span>
        <span v-if="process.integrity_validation" class="text-muted font-italic">
          {{ $t('system.by') }}
          <span v-if="process.integrity_validation.created_by" qid="view-integrity-validation-by">
            {{ process.integrity_validation.created_by.name }}
          </span>
        </span>
        <span v-if="process.integrity_validation" class="text-muted font-italic">
          {{ $t('system.on') }}
          <span v-if="process.integrity_validation.created_at" qid="view-integrity-validation-date">
            {{ process.integrity_validation.created_at | formatDate }}
          </span>
        </span>
      </div>
    </div>
    <div class="row no-gutters">
      <div class="col-sm-12">
        <div class="card-body">

          <div class="mw-500" v-if="showForm">
            <label for="">{{ $t('processes.risk.impact_label') }}</label>
            <multiselect
                v-model="selectedImpact"
                :options="impactItems"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('processes.risk.impact_placeholder')"
                label="name"
                qid="integrity-form-impact"
            ></multiselect>
            <br>
            <label for="">{{ $t('processes.risk.probability_label') }}</label>
            <multiselect
                v-model="selectedProbability"
                :options="probabilityItems"
                :select-label="$t('system.dropdown_select_label')"
                :selected-label="$t('system.dropdown_selected_label')"
                :deselect-label="$t('system.dropdown_deselect_label')"
                :placeholder="$t('processes.risk.probability_placeholder')"
                label="name"
                qid="integrity-form-probability"
            ></multiselect>
            <br>
            <label for="">{{ $t('processes.risk.description_label') }}</label>
            <b-form-textarea
              qid="integrity-form-description"
              v-model="description"
              rows="6"
              :placeholder="$t('maintenance.third_parties.create.description_placeholder')"
            ></b-form-textarea>
            <div class="text-right">
              <b-button class="mt-3"
                        variant="secondary"
                        @click="cancelForm"
                        qid="integrity-form-cancel-button"
              >
                {{ $t('processes.legal_basis.create.cancel') }}
              </b-button>
              <b-button class="mt-3 ml-3"
                        variant="success"
                        @click="formSubmit"
                        qid="integrity-form-submit-button"
              >
                {{ $t('processes.legal_basis.create.submit') }}
              </b-button>
            </div>
          </div>

          <div v-if="!showForm">
            <div v-if="process.gdpr">
              <label for="">{{ $t('processes.risk.impact_label') }}</label>
              <p class="section-container">
                <span v-if="process.gdpr.integrity_impact">
                  {{ $t('impact.' + $options.filters.slugify(process.gdpr.integrity_impact)) }}
                </span>
                <span v-if="!process.gdpr.integrity_impact" class="text-muted">
                  {{ $t('system.missing_input') }}
                </span>
                &nbsp;
              </p>

              <br>
              <label for="">{{ $t('processes.risk.probability_label') }}</label>
              <p class="section-container">
                <span v-if="process.gdpr.integrity_probability">
                  {{ $t('probability.' + $options.filters.slugify(process.gdpr.integrity_probability)) }}
                </span>
                <span v-if="!process.gdpr.integrity_probability" class="text-muted">
                  {{ $t('system.missing_input') }}
                </span>
                &nbsp;
              </p>

              <br>
              <label for="">{{ $t('processes.risk.risk_label') }}</label>
              <p qid="integrity-risk-value" class="section-container">
                <font-awesome-icon
                  icon="exclamation-triangle"
                  v-if="process.gdpr.integrity_risk.number"
                  :class="[ process.gdpr.integrity_risk.number ? 'risk-'+process.gdpr.integrity_risk.number : 'risk' ]"
                />
                <span v-if="process.gdpr.integrity_risk.risk">
                  {{ $t('risk.' + $options.filters.slugify(process.gdpr.integrity_risk.risk)) }}
                </span>
                <span v-if="process.gdpr.integrity_risk.number">
                  ({{process.gdpr.integrity_risk.number}})
                </span>
                <span v-if="!process.gdpr.integrity_risk.risk" class="text-muted">
                  {{ $t('system.missing_input') }}
                </span>
                &nbsp;
              </p>
              <br>
              <label for="">{{ $t('processes.risk.description_label') }}</label>
              <p class="text-formatted section-container" v-if="process.gdpr.integrity_impact_description">{{process.gdpr.integrity_impact_description}}</p>
              <p v-if="!process.gdpr.integrity_impact_description" class="text-muted section-container">
                {{ $t('system.missing_input') }}
              </p>
            </div>
          </div>
        </div>

        <div class="text-center mb-2 pt-2 card-body" v-if="!showOnTask">
          <span class="cursor-pointer"
                @click="validationShow = !validationShow"
                qid="expand-integrity-validations"
          >
            <span class="mr-2">
              {{ $t('system.view_validation_log') }}
            </span>
            <font-awesome-icon icon="chevron-down" v-if="!validationShow"/>
            <font-awesome-icon icon="chevron-up" v-if="validationShow"/>
          </span>
        </div>
        <div v-show="validationShow && !showOnTask" class="card-body">
          <ValidationList
              :object="process"
              type="integrity"
              :reloadList="updateValidationList"
              @validationListUpdated="setValidation"
          ></ValidationList>
        </div>
      </div>

    </div>
  </div>
</div>

</template>

<script>
  import piincHttp from '@/util/configureAxios.js'
  import ValidationForm from '@/views/Validations/ValidationForm.vue';
  import ValidationList from '@/views/Validations/ValidationList.vue';
  import UpdateSingleTaskModal from '@/views/Tasks/UpdateSingleTaskModal.vue';
  import GuideButton from '@/components/guide/GuideButton.vue';


  export default {
    name: 'Integrity',
    data: function() {
      return {
        form: {},
        selectedImpact: [],
        selectedProbability: [],
        description: '',
        error: '',
        loadingItems: false,
        validationShow: false,
        updateValidationList: false,
        showForm: false,
        impactItems: [
          { name: this.$t('impact.very_low'), value: 'Very low' },
          { name: this.$t('impact.low'), value: 'Low' },
          { name: this.$t('impact.high'), value: 'High' },
          { name: this.$t('impact.very_high'), value: 'Very high' }
        ],
        probabilityItems: [
          { name: this.$t('probability.very_unlikely'), value: 'Very unlikely' },
          { name: this.$t('probability.unlikely'), value: 'Unlikely' },
          { name: this.$t('probability.likely'), value: 'Likely' },
          { name: this.$t('probability.very_likely'), value: 'Very likely' }
        ],
        validation: [],
        process: {},
        myGuides: [],
        myTasks: []
      }
    },
    props: {
      object: {
        type: Object
      },
      showOnTask: {},
      task: {}
    },
    computed: {
      getClient() {
        return this.$store.state.client
      },
      getReloadContent() {
        return this.$store.state.updateContent
      },
      getReloadTasks() {
        return this.$store.state.reloadTasks
      }
    },
    components: {
      ValidationForm,
      ValidationList,
      UpdateSingleTaskModal,
      GuideButton
    },
    watch: {
      object: function (value) {
        this.process = value
        this.setDefaultForm(value)
        this.loadTasks('update_loss_of_integrity');
      },
      getReloadContent: function () {
        if (!this.showOnTask) {
          if (this.$store.state.updatedObject && this.$store.state.updatedObject.id) {
            if (this.$store.state.updatedObject.id == this.process.id && this.$store.state.updatedObject.who_am_i == this.process.who_am_i) {
              this.process = this.$store.state.updatedObject
            }
          }
          this.reloadContent()
        }
      },
      getReloadTasks: function () {
        if (!this.showOnTask) {
          this.loadTasks('update_loss_of_integrity')
        }
      },
    },
    methods: {
      getTask(code) {
        let existingTask = _.find(this.myTasks, function(object) {
          return object.code == code
        });
        return existingTask
      },
      reloadContent() {
        this.loadProcess()
        this.loadTasks('update_loss_of_integrity');
      },
      setValidation(object) {
        this.process.integrity_validation = object
      },
      loadTasks(code) {
        let self = this;
        if (this.getClient.projectModule) {
          piincHttp.get('projects/me/tasks', { params:
            {
              per_page: 1,
              status: 'pending',
              model_type: this.process.who_am_i,
              model_id: this.process.id,
              code: code,
              client: this.getClient.slug
            }
          }).then(function(response) {
            self.myTasks = []
            if (response.data.items.length) {
              self.myTasks.push(response.data.items[0])
            }
          }, function() {});
        }
      },
      navigateToTask(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
        this.$router.push({ name: 'processes.view', params: { slug: object.model.slug }, query: { task: object.id }})
      },
      openToolbar(object) {
        this.$store.commit('storeTaskToolbarState', true);
        this.$store.commit('storeActiveTask', object);
      },
      getGuide(code) {
        let existingGuide = _.find(this.myGuides, function(object) {
          return object.scope == code && object.type == 'Process' && object.enabled
        });
        return existingGuide
      },
      loadGuides(code) {
        let self = this;
        let language = localStorage.getItem('lang')? localStorage.getItem('lang') : 'en';

        piincHttp.get('guides', { params:
          {
            per_page: 1,
            language: 'en',
            type: 'Process',
            scope: code,
            client: this.getClient.slug
          }
        }).then(function(response) {
          if (response.data.items.length) {
            self.myGuides.push(response.data.items[0])
          }
        }, function() {});
      },
      formSubmit(evt) {
        // Keep the reference in a variable
        // to use it inside function called when promise returned
        let self = this;
        evt.preventDefault();
        this.form.type = 'integrity';

        this.form.integrity_impact = ''
        this.form.integrity_probability = ''
        if (this.selectedImpact) {
          this.form.integrity_impact = this.selectedImpact.value;
        }
        if (this.selectedProbability) {
          this.form.integrity_probability = this.selectedProbability.value;
        }
        this.form.integrity_impact_description = this.description;

        piincHttp.put('processes/' + this.process.slug, this.form).then(function(response) {
          self.process = response.data
          self.$emit('updated');
          self.showForm = false;
          self.loadTasks('update_loss_of_integrity');
          self.$toastr('success', self.$t('processes.update.success'))
        }, function(error) {
          if (error.status !== 200) {
            self.error = error.data.message;
          }
        })
      },
      handleValidationModalClose() {
        this.loadProcess();
        this.updateValidationList = true
      },
      cancelForm() {
        this.showForm = false
      },
      showFormFunction() {
        this.setDefaultForm(this.object)
        this.showForm = true
      },
      loadProcess() {
        let self = this;
        piincHttp.get('processes/' + this.process.slug, { params:
          {
            client: this.getClient.slug
          }
        }).then(function(response) {
          self.process = response.data;
        }, function() {});
      },
      setDefaultForm(object) {
        if (object.gdpr.integrity_impact) {
          this.selectedImpact = _.find(this.impactItems, function(item) {
            return item.value === object.gdpr.integrity_impact;
          });
        }
        if (object.gdpr.integrity_probability) {
          this.selectedProbability = _.find(this.probabilityItems, function(item) {
            return item.value === object.gdpr.integrity_probability;
          });
        }
        this.description = this.process.gdpr.integrity_impact_description;

      }
    },
    created () {
      this.process = this.object

      if (this.process && this.process.slug) {
        this.loadTasks('update_loss_of_integrity');
      }

      this.loadGuides('update_loss_of_integrity');

    }
  }
</script>
